<template>
    <div class="tt">
        <div class="tt__header">
            <div class="tt__header-left">
                <svg class="tt__icon" width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="#C59014"/>
                </svg>
                <h3 class="tt__title">КОМАНДА</h3>
            </div>
            <div class="tt__controls">
                <div class="tt__toggle">
                    <button
                        type="button"
                        class="tt__toggle-btn"
                        :class="{ 'tt__toggle-btn--active': source === 'uaf_u19' }"
                        @click="setSource('uaf_u19')"
                    >
                        U-19 (UAF)
                    </button>
                    <button
                        type="button"
                        class="tt__toggle-btn"
                        :class="{ 'tt__toggle-btn--active': source === 'uaf_pro_liha' }"
                        @click="setSource('uaf_pro_liha')"
                    >
                        Про ліга (UAF)
                    </button>
                    <button
                        type="button"
                        class="tt__toggle-btn"
                        :class="{ 'tt__toggle-btn--active': source === 'koaf_vishcha' }"
                        @click="setSource('koaf_vishcha')"
                    >
                        Чемпіонат області
                    </button>
                </div>
                <select
                    v-if="source === 'uaf_u19' && availableSeasons.length"
                    v-model="selectedSeasonId"
                    class="tt__select"
                    @change="loadStandings"
                >
                    <option
                        v-for="season in availableSeasons"
                        :key="season.season_id"
                        :value="season.season_id"
                    >
                        {{ season.season }}
                    </option>
                </select>
                <select
                    v-if="source === 'uaf_pro_liha'"
                    v-model="selectedAge"
                    class="tt__select"
                    @change="onAgeChange"
                >
                    <option v-for="age in ageOptions" :key="age" :value="age">
                        {{ age }}
                    </option>
                </select>
            </div>
        </div>

        <div v-if="loading" class="tt__empty">
            <span>Завантаження таблиці...</span>
        </div>

        <div class="tt__table-wrap" v-else-if="standings.length > 0">
            <table class="tt__table">
                <thead>
                    <tr class="tt__thead-row">
                        <th class="tt__th tt__th--pos"></th>
                        <th class="tt__th tt__th--team">КОМАНДА</th>
                        <th class="tt__th">І</th>
                        <th class="tt__th">В</th>
                        <th class="tt__th">Н</th>
                        <th class="tt__th">П</th>
                        <th class="tt__th">Г</th>
                        <th class="tt__th tt__th--pts">ОЧ.</th>
                        <th class="tt__th tt__th--form">ФОРМА</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(team, index) in standings" :key="index"
                        class="tt__row"
                        :class="{'tt__row--ours': team.isOurTeam}">
                        <td class="tt__td tt__td--pos">
                            <span class="tt__pos-badge" :class="getPosClass(index + 1)">{{ index + 1 }}</span>
                        </td>
                        <td class="tt__td tt__td--team">
                            <div class="tt__team">
                                <img v-if="team.teamLogo" :src="team.teamLogo" class="tt__team-logo" alt="" />
                                <span v-else class="tt__team-logo tt__team-logo--placeholder"></span>
                                <span class="tt__team-name">{{ team.teamName }}</span>
                            </div>
                        </td>
                        <td class="tt__td">{{ team.played }}</td>
                        <td class="tt__td">{{ team.wins }}</td>
                        <td class="tt__td">{{ team.draws }}</td>
                        <td class="tt__td">{{ team.losses }}</td>
                        <td class="tt__td">{{ team.goalsFor }}:{{ team.goalsAgainst }}</td>
                        <td class="tt__td tt__td--pts">{{ team.points }}</td>
                        <td class="tt__td tt__td--form">
                            <div class="tt__form" v-if="team.form && team.form.length">
                                <span v-for="(r, i) in team.form" :key="i"
                                    class="tt__form-dot"
                                    :class="'tt__form-dot--' + String(r).toLowerCase()">
                                    {{ r === 'W' ? 'В' : r === 'D' ? 'Н' : 'П' }}
                                </span>
                            </div>
                            <span v-else class="tt__form-empty">—</span>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <div v-else class="tt__empty">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" opacity="0.3">
                <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" stroke="#C59014" stroke-width="1.5"/>
            </svg>
            <span>{{ errorMessage || 'Немає даних турнірної таблиці' }}</span>
        </div>
    </div>
</template>

<script>
import { ref, onMounted, inject, watch } from 'vue'
import { getExternalStandings } from '@/desktopApp/services/apiRequests'

export default {
    name: 'TournamentTable',

    setup() {
        const source = ref('uaf_u19')
        const selectedSeasonId = ref('')
        const selectedAge = ref('U17')
        const ageOptions = ['U14', 'U15', 'U16', 'U17']
        const availableSeasons = ref([])
        const standings = ref([])
        const loading = ref(false)
        const errorMessage = ref('')

        const sharedProLihaAge = inject('proLihaAge', null)

        const syncSharedAge = (age) => {
            if (sharedProLihaAge) {
                sharedProLihaAge.value = age
            }
        }

        const loadStandings = async () => {
            loading.value = true
            errorMessage.value = ''
            try {
                const options = {}
                if (source.value === 'uaf_u19' && selectedSeasonId.value) {
                    options.season = selectedSeasonId.value
                }
                if (source.value === 'uaf_pro_liha') {
                    options.age = selectedAge.value
                    syncSharedAge(selectedAge.value)
                }

                const data = await getExternalStandings(source.value, options)
                standings.value = data?.standings || []

                if (data?.availableSeasons?.length) {
                    availableSeasons.value = data.availableSeasons
                    if (source.value === 'uaf_u19') {
                        const hasSelected = availableSeasons.value.some(s => s.season_id === selectedSeasonId.value)
                        if (!hasSelected) {
                            selectedSeasonId.value = data.seasonId || availableSeasons.value[0]?.season_id || ''
                        }
                    }
                }

                if (data?.error) {
                    errorMessage.value = data.error
                }
            } catch (e) {
                standings.value = []
                errorMessage.value = 'Не вдалося завантажити таблицю'
            } finally {
                loading.value = false
            }
        }

        const setSource = async (nextSource) => {
            if (source.value === nextSource) return
            source.value = nextSource
            await loadStandings()
        }

        const onAgeChange = async () => {
            syncSharedAge(selectedAge.value)
            await loadStandings()
        }

        const getPosClass = (pos) => {
            if (pos <= 3) return 'tt__pos-badge--top'
            if (pos >= 10) return 'tt__pos-badge--bottom'
            return ''
        }

        if (sharedProLihaAge) {
            watch(sharedProLihaAge, (nextAge) => {
                if (!nextAge || nextAge === selectedAge.value) return
                selectedAge.value = nextAge
                if (source.value === 'uaf_pro_liha') {
                    loadStandings()
                }
            })
        }

        onMounted(() => {
            syncSharedAge(selectedAge.value)
            loadStandings()
        })

        return {
            source,
            selectedSeasonId,
            selectedAge,
            ageOptions,
            availableSeasons,
            standings,
            loading,
            errorMessage,
            loadStandings,
            setSource,
            onAgeChange,
            getPosClass
        }
    }
}
</script>

<style scoped>
.tt {
    background: linear-gradient(180deg, #1c1c1e 0%, #161618 100%);
    border: 1px solid rgba(255, 255, 255, 0.04);
    border-radius: 16px;
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.tt__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    padding: 14px 18px;
    background: rgba(197, 144, 20, 0.06);
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
    flex-wrap: wrap;
}

.tt__header-left {
    display: flex;
    align-items: center;
    gap: 8px;
}

.tt__icon {
    flex-shrink: 0;
}

.tt__title {
    color: #fff;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 1.2px;
    text-transform: uppercase;
}

.tt__controls {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
}

.tt__toggle {
    display: flex;
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 10px;
    overflow: hidden;
}

.tt__toggle-btn {
    background: transparent;
    border: none;
    color: rgba(255, 255, 255, 0.45);
    font-size: 11px;
    font-weight: 600;
    padding: 6px 10px;
    cursor: pointer;
    transition: all 0.2s;
    white-space: nowrap;
}

.tt__toggle-btn--active {
    background: rgba(197, 144, 20, 0.25);
    color: #C59014;
}

.tt__select {
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    color: #ccc;
    padding: 5px 10px;
    border-radius: 8px;
    font-size: 11px;
    outline: none;
    cursor: pointer;
    transition: border-color 0.2s;
}

.tt__select:hover {
    border-color: rgba(197, 144, 20, 0.4);
}

.tt__table-wrap {
    flex: 1;
    overflow-y: auto;
    scrollbar-width: thin;
    scrollbar-color: #333 transparent;
}

.tt__table {
    width: 100%;
    border-collapse: collapse;
}

.tt__thead-row {
    position: sticky;
    top: 0;
    z-index: 1;
    background: #1a1a1c;
}

.tt__th {
    color: rgba(255, 255, 255, 0.35);
    font-weight: 600;
    text-align: center;
    padding: 10px 5px;
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.tt__th--pos { width: 36px; }
.tt__th--team { text-align: left; padding-left: 12px; }
.tt__th--pts { color: rgba(197, 144, 20, 0.6); }
.tt__th--form { width: 100px; }

.tt__row {
    transition: background-color 0.15s;
}

.tt__row:hover {
    background: rgba(255, 255, 255, 0.02);
}

.tt__row--ours {
    background: rgba(143, 196, 8, 0.06);
    border-left: 2px solid #8FC408;
}

.tt__td {
    color: rgba(255, 255, 255, 0.8);
    text-align: center;
    padding: 8px 5px;
    font-size: 12px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.03);
}

.tt__td--pos { width: 36px; }
.tt__td--team { text-align: left; padding-left: 12px; }
.tt__td--pts { font-weight: 800; color: #fff; font-size: 13px; }

.tt__pos-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border-radius: 6px;
    font-size: 10px;
    font-weight: 700;
    color: rgba(255, 255, 255, 0.5);
    background: rgba(255, 255, 255, 0.04);
}

.tt__pos-badge--top {
    background: linear-gradient(135deg, #339989 0%, #2b7d70 100%);
    color: #fff;
    box-shadow: 0 2px 8px rgba(51, 153, 137, 0.3);
}

.tt__pos-badge--bottom {
    background: linear-gradient(135deg, #DE3A73 0%, #b82d5c 100%);
    color: #fff;
    box-shadow: 0 2px 8px rgba(222, 58, 115, 0.3);
}

.tt__team {
    display: flex;
    align-items: center;
    gap: 8px;
}

.tt__team-logo {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    object-fit: cover;
    border: 1px solid rgba(255, 255, 255, 0.1);
}

.tt__team-logo--placeholder {
    display: block;
    background: rgba(255, 255, 255, 0.08);
}

.tt__team-name {
    font-size: 12px;
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 150px;
}

.tt__form {
    display: flex;
    gap: 4px;
    justify-content: center;
}

.tt__form-empty {
    color: rgba(255, 255, 255, 0.2);
    font-size: 12px;
}

.tt__form-dot {
    width: 18px;
    height: 18px;
    border-radius: 4px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 9px;
    font-weight: 800;
    color: #fff;
}

.tt__form-dot--w { background: #339989; box-shadow: 0 0 6px rgba(51, 153, 137, 0.4); }
.tt__form-dot--d { background: #C59014; box-shadow: 0 0 6px rgba(197, 144, 20, 0.4); }
.tt__form-dot--l { background: #DE3A73; box-shadow: 0 0 6px rgba(222, 58, 115, 0.4); }

.tt__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    gap: 12px;
    color: rgba(255, 255, 255, 0.25);
    font-size: 13px;
    padding: 24px;
    text-align: center;
}
</style>
